/**
 * m3 echo Frontend - Job Detail Dialog (Refactored)
 *
 * Zeigt Job-Details mit Balkendiagramm und Analyse-Ergebnissen.
 * Refactored to use React Query hooks and Zustand stores.
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  LinearProgress,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LineChart,
  Line,
} from 'recharts';
import { API_BASE_URL } from '../services/api';
import { translateStatus } from '../utils/statusTranslations';

// New hooks and stores
import { useJobWithProgress, useJobHistory } from '../hooks/useJobs';
import { useUIStore } from '../stores/useUIStore';
import { useModelCatalog } from '../hooks/useModelCatalog';
import { useCitationModuleActive } from '../hooks/useCitations';
import CitationSourceList from './CitationSourceList';
import { safeHttpUrl } from '../utils/safeUrl';
import { calculateMentionScore } from '../utils/mentionScore';

const JobDetailDialog: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { active: citationModuleActive } = useCitationModuleActive();
  const { modelLabel } = useModelCatalog();
  // Get dialog state from Zustand store
  const { jobDetailDialogOpen, selectedJobId, closeJobDetailDialog } = useUIStore();

  // Use React Query hooks for data fetching
  const { data: job, isLoading, progress } = useJobWithProgress(selectedJobId || undefined);
  const { data: history } = useJobHistory(selectedJobId || undefined);

  /**
   * Handles dialog close
   */
  const handleClose = () => {
    closeJobDetailDialog();
  };

  /**
   * Formats date for display
   */
  const formatDate = (dateString: string): string => {
    const utcDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const date = new Date(utcDateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  /**
   * Gets status color
   */
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'running':
        return 'primary';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  /**
   * Gets color for score (0-100 scale)
   */
  const getScoreColor = (score: number | null): string => {
    if (score === null) return '#bdbdbd'; // grey: failed result, not evaluable
    if (score >= 75) return '#4caf50'; // green
    if (score >= 50) return '#8bc34a'; // light green
    if (score >= 25) return '#ffc658'; // yellow
    if (score >= 1) return '#ff9800'; // orange
    return '#f44336'; // red
  };

  /**
   * Normalizes text by removing diacritics (accents) for comparison
   * e.g., "Telefónica" -> "Telefonica"
   */
  const normalizeText = (text: string): string => {
    return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  };

  /**
   * Highlights search terms in text with yellow background
   * Supports multiple search terms (main + synonyms) - v4.8.0
   * Ignores accents/diacritics when matching (e.g., ó matches o)
   */
  const highlightSearchTerms = (text: string, searchTerms: string[]): React.ReactNode => {
    if (!searchTerms || searchTerms.length === 0 || !text) return text;

    // Filter out empty terms
    const validTerms = searchTerms.filter(term => term && term.trim());
    if (validTerms.length === 0) return text;

    // Create normalized patterns for matching (ignores accents)
    const normalizedTerms = validTerms.map(term => normalizeText(term.toLowerCase()));
    const escapedTerms = normalizedTerms.map(term => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

    // Find all matches in the normalized text
    const normalizedText = normalizeText(text);
    const regex = new RegExp(`(${escapedTerms.join('|')})`, 'gi');

    // Build result by finding matches in normalized text and mapping back to original
    const result: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(normalizedText)) !== null) {
      // Add text before this match
      if (match.index > lastIndex) {
        result.push(text.slice(lastIndex, match.index));
      }
      // Add highlighted match (use original text, not normalized)
      const originalMatch = text.slice(match.index, match.index + match[0].length);
      result.push(
        <span key={match.index} style={{ backgroundColor: '#ffeb3b', padding: '2px 4px' }}>
          {originalMatch}
        </span>
      );
      lastIndex = match.index + match[0].length;
    }

    // Add remaining text after last match
    if (lastIndex < text.length) {
      result.push(text.slice(lastIndex));
    }

    return result.length > 0 ? result : text;
  };

  /**
   * Prepares data for bar chart
   */
  const prepareChartData = () => {
    if (!job || !job.results) return [];

    // A failed result has no score: no bar value, grey cell - not a 0.
    return job.results.map((result) => ({
      model: modelLabel(result.model_name),
      score: result.error != null
        ? null
        : calculateMentionScore(result.analysis_result.mentioned, result.analysis_result.position),
    }));
  };

  /**
   * Prepares data for history trend chart
   */
  const prepareHistoryTrendData = () => {
    if (!history || !history.chain) return [];

    return history.chain.map((item, index) => ({
      name: `#${index + 1}`,
      score: item.average_score,
      date: formatDate(item.job.created_at),
    }));
  };

  /**
   * Renders PDF generation button
   */
  const renderPdfButton = () => {
    if (!job) return null;

    const pdfUrl = `${API_BASE_URL}/api/jobs/${job.id}/export/pdf`;

    return (
      <Button
        variant="outlined"
        startIcon={<PictureAsPdfIcon />}
        href={pdfUrl}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ mr: 1 }}
      >
        PDF Export
      </Button>
    );
  };

  // Don't render if dialog is not open
  if (!jobDetailDialogOpen) return null;

  const chartData = prepareChartData();
  const historyTrendData = prepareHistoryTrendData();

  return (
    <Dialog open={jobDetailDialogOpen} onClose={handleClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        {isLoading ? 'Lade Job-Details...' : `Job #${job?.id || ''}`}
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 1, sm: 2 } }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : job ? (
          <>
            {/* Status and Basic Info */}
            <Box mb={3}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3} sx={{ display: 'flex' }}>
                  <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Status
                    </Typography>
                    <Chip
                      label={translateStatus(job.status)}
                      color={getStatusColor(job.status) as any}
                      sx={{ mt: 1 }}
                    />
                    {(job.status === 'running' || job.status === 'pending') && progress && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" display="block" gutterBottom>
                          {progress.message}
                        </Typography>
                        {progress.current !== undefined && progress.total !== undefined && progress.total > 0 && (
                          <LinearProgress
                            variant="determinate"
                            value={(progress.current / progress.total) * 100}
                            sx={{ mt: 1 }}
                          />
                        )}
                      </Box>
                    )}
                  </Paper>
                </Grid>
                <Grid item xs={12} md={3} sx={{ display: 'flex' }}>
                  <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Durchschnittliche Punktzahl
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      {job.average_score != null ? job.average_score.toFixed(2) : (job.status === 'completed' || job.status === 'failed' ? 'nicht auswertbar' : '-')}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={3} sx={{ display: 'flex' }}>
                  <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Suchbegriff
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      {job.search_term}
                      {(job.search_term_synonym1 || job.search_term_synonym2) && (
                        <Typography component="span" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                          {' '}({[job.search_term_synonym1, job.search_term_synonym2].filter(Boolean).join(', ')})
                        </Typography>
                      )}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={3} sx={{ display: 'flex' }}>
                  <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Erstellt am
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      {formatDate(job.created_at)}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>

            {/* Bar Chart */}
            {chartData.length > 0 && (
              <Box mb={3}>
                <Typography variant="h6" gutterBottom>
                  Ergebnisse nach Modell
                </Typography>
                <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="model" tick={{ fontSize: isMobile ? 10 : 12 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: isMobile ? 10 : 12 }} />
                    <Tooltip />
                    {!isMobile && <Legend />}
                    <Bar dataKey="score" name="Punktzahl">
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getScoreColor(entry.score)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}

            {/* History Trend Chart */}
            {history && history.chain && history.chain.length > 1 && (
              <Box mb={3}>
                <Typography variant="h6" gutterBottom>
                  Verlauf (Original + Re-Tests)
                </Typography>
                <ResponsiveContainer width="100%" height={isMobile ? 180 : 250}>
                  <LineChart data={historyTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: isMobile ? 10 : 12 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: isMobile ? 10 : 12 }} />
                    <Tooltip />
                    {!isMobile && <Legend />}
                    <Line
                      type="monotone"
                      dataKey="score"
                      stroke="#8884d8"
                      name="Durchschnittliche Punktzahl"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            )}

            {/* Detailed Results - Grouped by Prompt */}
            <Typography variant="h6" gutterBottom>
              Detaillierte Ergebnisse
            </Typography>
            {job.results && job.results.length > 0 ? (
              (() => {
                // Group results by prompt_id
                const groupedResults = job.results.reduce((acc, result) => {
                  const promptId = result.prompt_id || 0;
                  if (!acc[promptId]) {
                    acc[promptId] = [];
                  }
                  acc[promptId].push(result);
                  return acc;
                }, {} as Record<number, typeof job.results>);

                // Get prompt info for each group
                const getPromptInfo = (promptId: number) => {
                  if (promptId === 0) return { id: 0, content: 'Unbekannter Prompt' };
                  const jobPrompt = job.job_prompts?.find(jp => jp.prompt_id === promptId);
                  if (!jobPrompt) return { id: promptId, content: `Prompt #${promptId}` };

                  // Replace placeholders in prompt content with actual values
                  let displayContent = jobPrompt.prompt_content_snapshot || jobPrompt.prompt.content;
                  if (jobPrompt.prompt_values) {
                    Object.entries(jobPrompt.prompt_values).forEach(([key, value]) => {
                      displayContent = displayContent.replace(new RegExp(`\\[${key}\\]`, 'g'), value);
                    });
                  }
                  return { id: promptId, content: displayContent };
                };

                const promptIds = Object.keys(groupedResults).map(Number).sort();

                return promptIds.map((promptId, groupIndex) => {
                  const promptInfo = getPromptInfo(promptId);
                  const results = groupedResults[promptId];

                  return (
                    <Box key={promptId} mb={3}>
                      {/* Prompt Header */}
                      <Paper sx={{ p: 2, mb: 2, backgroundColor: 'primary.light', color: 'primary.contrastText' }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          Prompt {groupIndex + 1}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                          {promptInfo.content.length > 150
                            ? `${promptInfo.content.substring(0, 150)}...`
                            : promptInfo.content}
                        </Typography>
                      </Paper>

                      {/* Results for this prompt */}
                      {results.map((result, resultIndex) => {
                        const failed = result.error != null;
                        const score = failed
                          ? null
                          : calculateMentionScore(result.analysis_result.mentioned, result.analysis_result.position);
                        return (
                          <Accordion
                            key={`${promptId}-${resultIndex}`}
                            defaultExpanded={!isMobile && groupIndex === 0 && resultIndex === 0}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Box display="flex" alignItems="center" width="100%">
                                <Typography sx={{ flexGrow: 1 }}>
                                  {modelLabel(result.model_name)}
                                </Typography>
                                <Chip
                                  label={score === null ? 'nicht auswertbar' : `Punktzahl: ${score}`}
                                  sx={{
                                    backgroundColor: getScoreColor(score),
                                    color: 'white',
                                    mr: 2,
                                  }}
                                />
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Box>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  Erwähnt
                                </Typography>
                                <Typography variant="body1" gutterBottom>
                                  {failed ? '–' : result.analysis_result.mentioned ? 'Ja' : 'Nein'}
                                </Typography>

                                <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                                  Position
                                </Typography>
                                <Typography variant="body1" gutterBottom>
                                  {result.analysis_result.position || '-'}
                                </Typography>

                                <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                                  Sentiment
                                </Typography>
                                <Typography variant="body1" gutterBottom>
                                  {result.analysis_result.sentiment || '-'}
                                </Typography>

                                {result.analysis_result.context && (
                                  <>
                                    <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                                      Kontext
                                    </Typography>
                                    <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                                      <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                                        {result.analysis_result.context}
                                      </Typography>
                                    </Paper>
                                  </>
                                )}

                                <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                                  Antwort LLM
                                </Typography>
                                <Paper sx={{ p: 2, backgroundColor: 'grey.50', maxHeight: '200px', overflow: 'auto' }}>
                                  <Typography variant="body2" style={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>
                                    {highlightSearchTerms(result.raw_response, [job.search_term, job.search_term_synonym1, job.search_term_synonym2].filter(Boolean) as string[])}
                                  </Typography>
                                </Paper>

                                {/* Verwendete Quellen */}
                                {result.sources && result.sources.length > 0 && (
                                  <>
                                    <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                                      Verwendete Quellen
                                    </Typography>
                                    <Paper sx={{ p: 2, backgroundColor: 'grey.50', maxHeight: '150px', overflow: 'auto' }}>
                                      {citationModuleActive ? (
                                        <CitationSourceList sources={result.sources} />
                                      ) : (
                                        result.sources.map((source, idx) => (
                                          <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                                            <LinkIcon fontSize="small" color="action" sx={{ mt: 0.3, flexShrink: 0 }} />
                                            <Box>
                                              {safeHttpUrl(source.url) ? (
                                                <Typography
                                                  variant="body2"
                                                  component="a"
                                                  href={safeHttpUrl(source.url) as string}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                                >
                                                  {source.title || source.url}
                                                  <OpenInNewIcon sx={{ fontSize: '0.875rem' }} />
                                                </Typography>
                                              ) : (
                                                <Typography variant="body2">{source.title || source.url}</Typography>
                                              )}
                                              {source.snippet && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                  {source.snippet.substring(0, 100)}{source.snippet.length > 100 ? '...' : ''}
                                                </Typography>
                                              )}
                                            </Box>
                                          </Box>
                                        ))
                                      )}
                                    </Paper>
                                  </>
                                )}

                                {result.error && (
                                  <>
                                    <Typography variant="body2" color="error" gutterBottom sx={{ mt: 2 }}>
                                      Fehler
                                    </Typography>
                                    <Alert severity="error">
                                      {result.error}
                                    </Alert>
                                  </>
                                )}
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </Box>
                  );
                });
              })()
            ) : (
              <Alert severity="info">Keine Ergebnisse verfügbar</Alert>
            )}
          </>
        ) : (
          <Alert severity="error">Job konnte nicht geladen werden</Alert>
        )}
      </DialogContent>

      <DialogActions>
        {renderPdfButton()}
        <Button onClick={handleClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};

export default JobDetailDialog;
